import React, { useState } from 'react';
import { Sparkles } from 'lucide-react';
import './App.css';

import { Analytics } from '@vercel/analytics/react';

function App() {
  const [isSpinning, setIsSpinning] = useState(false);
  const [selectedSaying, setSelectedSaying] = useState(null);
  const [rotation, setRotation] = useState(Math.random() * 360);
  const [whimsyMode, setWhimsyMode] = useState(false);
  const [particles, setParticles] = useState([]);

  const sayings = [
    { text: "Is this based in data? Or vibes?", blueColor: "bg-blue-100", brightColor: "bg-red-400" },
    { text: "Would the person you feel you've given feedback to agree that you've given them feedback?", blueColor: "bg-blue-200", brightColor: "bg-green-500" },
    { text: "Tell me more…", blueColor: "bg-blue-300", brightColor: "bg-yellow-400" },
    { text: "So, help me understand….", blueColor: "bg-blue-400", brightColor: "bg-green-400" },
    { text: "Are you wanting to be right? Or do you want to be effective?", blueColor: "bg-blue-500", brightColor: "bg-blue-400" },
    { text: "Let's imagine a world where…", blueColor: "bg-blue-600", brightColor: "bg-purple-400" },
    { text: "Can you act as if you're already the thing you want to be?", blueColor: "bg-blue-300", brightColor: "bg-pink-400" },
    { text: "Which option makes you stand up straighter?", blueColor: "bg-blue-400", brightColor: "bg-red-500" },
    { text: "What would make this 10% more enjoyable?", blueColor: "bg-blue-200", brightColor: "bg-purple-300" },
    { text: "If you had an inch more autonomy, what would you do?", blueColor: "bg-blue-500", brightColor: "bg-yellow-500" },
    { text: "What else could be true?", blueColor: "bg-blue-300", brightColor: "bg-green-500" },
    { text: "What else could be possible?", blueColor: "bg-blue-600", brightColor: "bg-purple-500" },
    { text: "What decision leads to a more interesting future?", blueColor: "bg-blue-400", brightColor: "bg-pink-500" }
  ];

  const spinWheel = () => {
    if (isSpinning) return;
    
    setIsSpinning(true);
    setSelectedSaying(null);
    
    const randomSpins = 1440 + Math.random() * 720;
    const randomIndex = Math.floor(Math.random() * sayings.length);
    const degreesPerSegment = 360 / sayings.length;
    const targetRotation = randomSpins + (randomIndex * degreesPerSegment);
    
    setRotation(targetRotation);
    
    setTimeout(() => {
      setIsSpinning(false);
      setSelectedSaying(sayings[randomIndex]);
    }, 3000);
  };

  const generateParticles = () => {
    const emojis = ['💖', '✨', '🌟', '⭐', '💫', '🌈', '☀️', '🦋', '🌸', '💐', '🎈', '🎉', '😊', '💕', '🌺'];
    const newParticles = [];
    for (let i = 0; i < 20; i++) {
      newParticles.push({
        id: Date.now() + i,
        top: Math.random() * 100,
        delay: Math.random() * 2,
        duration: 3 + Math.random() * 2,
        emoji: emojis[Math.floor(Math.random() * emojis.length)],
        rotation: Math.random() * 360
      });
    }
    setParticles(newParticles);
  };

  const toggleWhimsyMode = () => {
    setWhimsyMode(!whimsyMode);
    if (!whimsyMode) {
      generateParticles();
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-8 relative overflow-hidden">
      
      {/* Whimsy mode particles */}
      {whimsyMode && particles.map(particle => (
        <div
          key={particle.id}
          className="absolute pointer-events-none text-4xl"
          style={{
            top: `${particle.top}%`,
            left: '-10%',
            animation: `slideAcross ${particle.duration}s linear ${particle.delay}s infinite`,
            transform: `rotate(${particle.rotation}deg)`
          }}
        >
          {particle.emoji}
        </div>
      ))}
      
      <h1 className={`text-5xl font-bold mb-4 flex items-center gap-3 mobile-title ${whimsyMode ? 'bg-gradient-to-r from-red-500 via-yellow-500 via-green-500 via-blue-500 to-purple-500 bg-clip-text text-transparent' : 'text-blue-900'}`}>
        <Sparkles className={`w-10 h-10 ${whimsyMode ? 'text-pink-500' : 'text-blue-600'}`} />
        What Would Sarah Say?
        <Sparkles className={`w-10 h-10 ${whimsyMode ? 'text-pink-500' : 'text-blue-600'}`} />
      </h1>
      <p className="text-blue-700 text-xl mb-4">spin for a dose of sarahisms</p>
      
      <button
        onClick={toggleWhimsyMode}
        className={`mb-8 px-6 py-2 rounded-full font-semibold transition-all ${
          whimsyMode 
            ? 'bg-gradient-to-r from-pink-400 via-purple-400 to-blue-400 text-white shadow-lg' 
            : 'bg-white text-blue-700 border-2 border-blue-300 hover:border-blue-500'
        }`}
      >
        {whimsyMode ? '✨ Whimsy Mode ON ✨' : 'Enable Whimsy Mode'}
      </button>
      
      <div className="relative mb-8">
        {/* Pointer arrow at top */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-8 z-20">
          <div className="w-0 h-0 border-l-[20px] border-l-transparent border-r-[20px] border-r-transparent border-t-[30px] border-t-blue-600"></div>
        </div>
        
        {/* Spinner wheel */}
        <div 
          className="relative w-96 h-96 rounded-full cursor-pointer shadow-2xl mx-auto"
          onClick={spinWheel}
          style={{
            transform: `rotate(${rotation}deg)`,
            transition: isSpinning ? 'transform 3s cubic-bezier(0.25, 0.1, 0.25, 1)' : 'none'
          }}
        >
          {sayings.map((saying, index) => {
            const angle = (360 / sayings.length) * index;
            return (
              <div
                key={index}
                className={`absolute w-full h-full ${whimsyMode ? saying.brightColor : saying.blueColor}`}
                style={{
                  clipPath: `polygon(50% 50%, 50% 0%, ${50 + 50 * Math.sin((Math.PI * 2) / sayings.length)}% ${50 - 50 * Math.cos((Math.PI * 2) / sayings.length)}%)`,
                  transform: `rotate(${angle}deg)`,
                  transformOrigin: 'center center'
                }}
              ></div>
            );
          })}
          
          {/* Center circle - INSIDE the wheel so it rotates with it */}
          <div className="spin-button-center bg-white rounded-full shadow-lg flex items-center justify-center border-4 border-blue-600" onClick={spinWheel}>
            <div className="text-xl font-bold text-blue-600">SPIN</div>
          </div>
        </div>
      </div>
      
      {/* Card popup */}
      {selectedSaying && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-30 animate-fadeIn">
          <div className="bg-white rounded-3xl p-12 shadow-2xl max-w-md transform animate-scaleIn">
            <div className="text-center">
              <Sparkles className="w-16 h-16 mx-auto mb-6 text-blue-500" />
              <p className="text-gray-600 mb-4">Sarah would say...</p>
              <h2 className="text-4xl font-bold text-gray-800 mb-4">{selectedSaying.text}</h2>
              <button
                onClick={() => setSelectedSaying(null)}
                className="bg-blue-600 text-white px-8 py-3 rounded-full font-semibold hover:bg-blue-700 transition-all"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Attribution */}
      <div className="mt-8 w-full">
        <div className="text-xs text-gray-500 text-center">
          <strong>
            👾 A{' '}
            <a 
              href="https://github.com/cloudhandler" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800 underline"
            >
              cloudhandler
            </a>
            {' '}project 👾
          </strong>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scaleIn {
          from { transform: scale(0.8); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        @keyframes slideAcross {
          from { 
            left: -10%;
            opacity: 0;
          }
          10% {
            opacity: 1;
          }
          90% {
            opacity: 1;
          }
          to { 
            left: 110%;
            opacity: 0;
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
        .animate-scaleIn {
          animation: scaleIn 0.3s ease-out;
        }
      `}</style>

     <Analytics /> 
     
    </div>
  );
}

export default App;